import { useEffect } from 'react';
import { getMessagingInstance, getToken, onMessage } from '../config/firebase';
import unifiedApi from '../services/unifiedApi';
import { showAdminNotificationToastOnce } from '../utils/adminNotificationToast';
import logger from '@/utils/logger';

// Enregistre le token Web Push (FCM) de l'admin. Sert principalement à activer
// le canal "onglet fermé/arrière-plan" (géré par public/firebase-messaging-sw.js),
// mais affiche aussi un toast en foreground (onMessage) — le SDK Firebase Web
// n'affiche PAS de notification OS automatiquement quand l'onglet a le focus,
// contrairement au cas arrière-plan. showAdminNotificationToastOnce()
// dédoublonne par notification_id avec useAdminRealtimeNotifications
// (WebSocket) au cas où les deux canaux livrent le même événement au même
// onglet. Voir docs/ideas/admin-realtime-notifications.md.
//
// IMPORTANT : Notification.requestPermission() appelé sans geste utilisateur
// (ex: au montage d'un composant) est silencieusement ignoré par les
// heuristiques anti-spam de Chrome — aucune popup, aucune erreur, aucun retry
// possible ensuite. `requestWebPushPermission({ promptIfNeeded: true })` doit
// donc toujours être appelé depuis l'intérieur d'un vrai handler déclenché par
// un geste utilisateur — trois points d'entrée dans ce projet :
// 1. frontLogin.jsx, juste après un login réussi (le clic sur "Se connecter"
//    fournit le geste) — cas nominal : la demande s'affiche dès la connexion.
// 2. Le listener 'click' one-shot sur `document` ci-dessous (fallback pour
//    une session déjà authentifiée, ex: token persistant + rechargement de
//    page, où aucun login n'a lieu) — le premier clic sur n'importe quelle
//    page admin sert de geste.
// 3. `useWebPushRegistration()` lui-même, silencieusement, si la permission
//    est déjà 'granted' d'une session précédente (pas de popup à montrer).
//
// `registered` est un flag module-level (pas un ref de composant) : la
// demande est fire-and-forget et doit survivre au démontage de frontLogin.jsx
// (qui se démonte dès la navigation post-login, avant que le flux async —
// getToken, registerToken — n'ait fini).
let registered = false;

export async function requestWebPushPermission({ promptIfNeeded = false, navigate } = {}) {
  if (registered) {
    return;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    logger.debug('[useWebPushRegistration] VITE_FIREBASE_VAPID_KEY absente — Web Push désactivé pour le moment.');
    return;
  }

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      return;
    }

    if (typeof Notification === 'undefined' || Notification.permission === 'denied') {
      return;
    }
    if (Notification.permission !== 'granted' && !promptIfNeeded) {
      // Pas encore de permission et pas de geste utilisateur explicite : on
      // n'appelle PAS requestPermission() ici (serait silencieusement ignoré).
      return;
    }
    const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }

    const token = await getToken(messaging, { vapidKey });
    if (!token) {
      return;
    }

    await unifiedApi.fcm.registerToken({ token, device_name: navigator.userAgent });
    registered = true;

    onMessage(messaging, (payload) => {
      logger.debug('[useWebPushRegistration] Message reçu au premier plan', payload);
      showAdminNotificationToastOnce({
        id: payload?.data?.notification_id,
        title: payload?.notification?.title,
        message: payload?.notification?.body,
        navigate,
      });
    });
  } catch (error) {
    logger.error('[useWebPushRegistration] Erreur enregistrement Web Push:', error);
  }
}

// Monté dans Header.jsx (présent sur toutes les pages admin). Couvre les cas
// où requestWebPushPermission() n'a pas été appelé au login (session déjà
// authentifiée avant rechargement de page, token persistant sans re-login).
export function useWebPushRegistration({ enabled = true, navigate } = {}) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Silencieux : uniquement si la permission est déjà accordée.
    requestWebPushPermission({ navigate });

    let removeClickListener;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const handleFirstInteraction = () => {
        requestWebPushPermission({ promptIfNeeded: true, navigate });
      };
      document.addEventListener('click', handleFirstInteraction, { once: true });
      removeClickListener = () => document.removeEventListener('click', handleFirstInteraction);
    }

    return () => removeClickListener?.();
  }, [enabled, navigate]);
}
